import { Award, FlaskRound, ShieldCheck, Thermometer, ScanLine, Droplets, FileCheck2, BadgeCheck } from "lucide-react";
import { Reveal, SectionHead, PageHero, SpecTable, CTASection, usePageMeta } from "../components/ui-bits";
import { IMG, ndtCapabilities, mechanicalTests } from "../data/content";

const Quality = () => {
  usePageMeta(
    "Quality & Testing — ASME U & U2, NDT, NABL Lab | G. R. Engineering",
    "ASME 'U' & 'U2' stamp holder. In-house NDT — radiography, UT, TOFD, PAUT, MT, PT, PMI, helium leak detection, hydrotest to 500 kg/cm²g — and an in-house NABL-accredited mechanical testing laboratory."
  );
  return (
    <>
      <PageHero
        img={IMG("bpcl-night")}
        eyebrow="Quality & Testing"
        title="Verified at every weld, proven at every test"
        sub="Quality assurance is engineered into every stage — from material identification to final hydrotest — backed by ASME accreditation and in-house laboratories."
      />

      <section className="bg-white border-b border-slate-200" data-testid="quality-accreditation">
        <div className="container-gre py-20 lg:py-24 grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <SectionHead
              eyebrow="Accreditation"
              title="ASME 'U' & 'U2' Stamp Holder"
              sub="G. R. Engineering holds ASME 'U' and 'U2' authorizations for the manufacture of pressure vessels — the foundation of our quality system for code-stamped equipment."
            />
            <Reveal delay={0.2}>
              <div className="mt-8 max-w-md">
                <SpecTable rows={[
                  ["ASME 'U' Stamp", "Certificate No. 38172"],
                  ["ASME 'U2' Stamp", "Certificate No. 41698"],
                  ["Validity (as documented)", "Through 07 Dec 2026"],
                  ["Management Systems", "Aligned to ISO 9001 / 14001 / 45001"],
                ]} />
              </div>
            </Reveal>
          </div>
          <div className="grid grid-cols-2 gap-px bg-slate-200 border border-slate-200">
            {[
              [Award, "ASME 'U'", "Pressure vessel manufacture"],
              [Award, "ASME 'U2'", "Alternative rules, Div. 2"],
              [BadgeCheck, "NABL Lab", "In-house mechanical testing"],
              [FileCheck2, "QA/QC Function", "Dedicated at Tarapur works"],
            ].map(([Icon, t, s], i) => (
              <Reveal key={t} delay={i * 0.06}>
                <div className="bg-white p-8 h-full">
                  <Icon size={26} className="text-[#C5221F]" strokeWidth={1.6} />
                  <p className="mt-5 font-display text-base font-bold text-slate-900">{t}</p>
                  <p className="mt-1.5 text-[13px] text-slate-600">{s}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-slate-50 border-b border-slate-200" data-testid="quality-ndt">
        <div className="container-gre py-20 lg:py-28">
          <SectionHead eyebrow="Non-Destructive Testing" title="In-house NDT — fourteen methods under one roof" sub="A separate radiography enclosure, isotope sources, advanced ultrasonics and leak detection keep examination inside the production schedule, not outside it." />
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200 border border-slate-200">
            {ndtCapabilities.map((n, i) => (
              <Reveal key={n} delay={(i % 3) * 0.05}>
                <div className="bg-white px-6 py-5 flex items-center gap-3.5 h-full">
                  <ScanLine size={16} className="text-[#C5221F] shrink-0" strokeWidth={1.75} />
                  <p className="text-sm font-medium text-slate-800">{n}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white border-b border-slate-200" data-testid="quality-mechanical">
        <div className="container-gre py-20 lg:py-28 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <img src={IMG("columns-hmel-c")} alt="Column section under fabrication at Tarapur works" className="w-full aspect-[4/3] object-cover img-industrial border border-slate-200" />
          </Reveal>
          <div>
            <SectionHead eyebrow="Mechanical Testing" title="In-house NABL-accredited laboratory" sub="Procedure and production test plates are broken, bent, pulled and frozen in-house — with impact testing down to −195 °C for cryogenic and low-temperature service." />
            <Reveal delay={0.2}>
              <div className="mt-8"><SpecTable rows={mechanicalTests} /></div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-[#0A1128] relative overflow-hidden" data-testid="quality-pressure">
        <div className="absolute inset-0 blueprint-grid" />
        <div className="container-gre relative py-20 lg:py-24">
          <SectionHead light eyebrow="Pressure & Integrity" title="Hydrotest. Helium. Pneumatic. Recorded." />
          <div className="mt-12 grid sm:grid-cols-3 gap-px bg-white/10 border border-white/10">
            {[
              [Droplets, "500 kg/cm² g", "Hydrostatic testing capability with time-vs-pressure recording"],
              [ScanLine, "Helium Leak Detection", "High-sensitivity leak verification for critical service"],
              [Thermometer, "PWHT / PFHT", "6 m × 6 m × 12 m bogie hearth furnace with chart recording"],
            ].map(([Icon, v, s], i) => (
              <Reveal key={v} delay={i * 0.08}>
                <div className="bg-[#0A1128]/70 backdrop-blur-sm p-8 h-full">
                  <Icon size={24} className="text-[#E8574F]" strokeWidth={1.75} />
                  <p className="mt-5 font-display text-2xl font-black text-white tracking-tight">{v}</p>
                  <p className="mt-2 text-[13px] text-slate-400 leading-relaxed">{s}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <p className="mt-10 max-w-3xl text-sm text-slate-400 leading-relaxed">
              Welding quality is controlled through qualified procedures, staged hold-point inspection, PMI and ferrite verification, and DFT coating thickness measurement — documented in the final quality dossier of every equipment.
            </p>
          </Reveal>
        </div>
      </section>

      <CTASection />
    </>
  );
};

export default Quality;
